import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { NextSeo } from "next-seo";

import Header from "../components_web/Header";
import Footer from "../components_web/Footer";
import SideMenu from "../components_web/SideMenu";
import { Constants } from "../src/config";
import { getClass } from "../src/helpers/formatHelpers";

const GamblingHelp = () => {
  const { t } = useTranslation();
  const [sideMenuShow, setSideMenuShow] = useState(false);
  const [sideMenuHeaderShow, setSideMenuHeaderShow] = useState(false);

  return (
    <div>
      <NextSeo
        title={`Gambling Help`}
        description={`Remember, gambling should be a form of leisurely entertainment and practiced in moderation. If you're in need of guidance, help is readily available.`}
        canonical={`https://${process.env.NEXT_PUBLIC_WWW_BASE_URL}/gambling-help`}
        openGraph={{
          type: "website",
          locale: "en_EN",
          url: `https://${process.env.NEXT_PUBLIC_WWW_BASE_URL}/gambling-help`,
          title: `Gambling Help - ${process.env.NEXT_PUBLIC_APP_NAME}`,
          description: `Remember, gambling should be a form of leisurely entertainment and practiced in moderation. If you're in need of guidance, help is readily available.`,
          images: [
            {
              url: `https://${process.env.NEXT_PUBLIC_WWW_BASE_URL}${process.env.NEXT_PUBLIC_BANNER_IMAGE}`,
              width: 1200,
              height: 630,
              alt: `${process.env.NEXT_PUBLIC_APP_NAME}${process.env.NEXT_PUBLIC_APP_SLOGAN}`,
              type: "image/png",
            },
          ],
          siteName: `${process.env.NEXT_PUBLIC_APP_NAME}${process.env.NEXT_PUBLIC_APP_SLOGAN}`,
        }}
      />
      <Header setSideMenuShow={setSideMenuShow} />

      <SideMenu
        sideMenuShow={sideMenuShow}
        setSideMenuShow={setSideMenuShow}
        sideMenuHeaderShow={sideMenuHeaderShow}
        setSideMenuHeaderShow={setSideMenuHeaderShow}
      />

      {/* GamblingHelp Section */}
      <div className={`${getClass("background5", true)}`} id="about">
        <div
          className={`wrapper mx-auto relative py-28 ${
            process.env.NEXT_PUBLIC_APP_NAME !== "Vybeful" &&
            process.env.NEXT_PUBLIC_APP_NAME !== "Festicket"
              ? "min-h-screen py-60 flex justify-center items-center"
              : ""
          }`}>
          <div className="flex flex-col font-regular text-grayText2 max-w-[1000px]">
            <h1
              className={`sm:text-3xl text-lg font-bold ${getClass(
                "text-white",
                true
              )} text-center sm:mb-6 mb-3`}>
              GAMBLING HELP
            </h1>
            <p className="font-bold">
              <strong>
                Remember, gambling should be a form of leisurely entertainment
                and practiced in moderation. If you&apos;re in need of guidance,
                help is readily available.
              </strong>
            </p>
            <h2 className="font-bold text-white my-5">
              <strong>Understanding Responsible Gambling</strong>
            </h2>
            <p className="mb-4">
              Gambling, in its ideal form, is a source of entertainment akin to
              movies, video games, and amusement parks. However, it can evolve
              into a serious addiction for some. The allure of slot
              machines&apos; lights and sounds, along with the thrill of table
              games, which require skill and interaction, can be captivating.
              Yet, for the majority, it remains a harmless diversion.
            </p>
            <p>
              The critical question is, &quot;At what point does casual gambling
              turn into a problem?&quot;. This shift occurs when gambling
              transitions from a recreational activity to a compulsive need.
              It&apos;s a common misconception that problem gambling is an issue
              that affects others, not oneself.
            </p>
            <h2 className="font-bold text-white my-5">
              <strong>Indicators of Addiction</strong>
            </h2>
            <p>
              Addiction, regardless of its form, presents noticeable signs.
              While some individuals may hide these signs effectively, they
              eventually become apparent.
            </p>
            <p className="mt-4">Indicators to watch for include:</p>
            <ul className="list-disc ml-6 my-3">
              <li>Overspending beyond financial means</li>
              <li>Acquiring loans to offset gambling losses</li>
              <li>Prioritizing gambling over enjoyment</li>
              <li>Excessive gambling habits</li>
              <li>Dishonesty about gambling activities</li>
              <li>Resorting to theft for gambling funds</li>
              <li>Pursuing lost money through more gambling</li>
              <li>Using gambling as an escape from personal issues</li>
            </ul>
            <p>
              Recognizing these behaviors in oneself might indicate a developed
              addiction.
            </p>
            <h2 className="font-bold text-white my-5">
              <strong>Roots of Gambling Addiction</strong>
            </h2>
            <p>
              Gambling addiction, like other addictions, often serves as an
              escape from daily stressors. Casinos and online gambling provide a
              temporary reprieve from reality. Factors that make individuals
              more prone to gambling addiction include:
            </p>
            <ul className="list-disc ml-6 my-3">
              <li>Genetic predisposition</li>
              <li>Depression</li>
              <li>Boredom</li>
              <li>Anxiety</li>
              <li>Personality disorders</li>
            </ul>
            <p>
              There are generally two types of compulsive gamblers: consistent
              gamblers and binge gamblers. Binge gamblers may deceive themselves
              into believing their habit is under control, yet they tend to
              spend excessively when they do gamble.
            </p>
            <h2 className="font-bold text-white my-5">
              <strong>The Risks Involved</strong>
            </h2>
            <p className="mb-4">
              It&apos;s crucial for gamblers to maintain self-awareness and
              recognize the signs of addiction. Gambling addiction can be
              challenging to identify, especially compared to substance abuse,
              which often has physical symptoms. Behavioral changes in a
              gambling addict, such as unexplained absences, erratic spending,
              financial difficulties, and mood fluctuations, may eventually
              become evident to their loved ones.
            </p>
            <p className="mb-4">
              Studies have indicated that the suicide rate among gambling
              addicts is significantly higher than that of individuals addicted
              to substances, often due to financial devastation, including loss
              of homes, jobs, and savings. This level of financial ruin can also
              lead to destroyed personal relationships.
            </p>
            <p>
              If you or someone you know displays these characteristics,
              it&apos;s crucial to seek help immediately. Support from close
              friends and family is vital in addressing and overcoming the
              problem.
            </p>
            <h2 className="font-bold text-white my-5">
              <strong>Self Exclusion and GamStop</strong>
            </h2>
            <p>
              Self-exclusion represents a proactive measure for individuals
              grappling with gambling issues, allowing them to voluntarily opt
              out of specific online gambling platforms. Players can initiate
              contact with gambling venues to request the permanent closure of
              their accounts or to enact a self-exclusion period, typically
              lasting a minimum of six months. Notably, the United Kingdom and
              Sweden are at the forefront of implementing robust self-exclusion
              programs. Detailed information about the{" "}
              <a
                className="underline"
                href="https://www.gamblingcommission.gov.uk/public-and-players/page/self-exclusion">
                protocols of the UK Gambling Commission
              </a>{" "}
              (UKGC) and the{" "}
              <a
                className="underline"
                href="https://www.casinonewsdaily.com/blog/2019/06/02/more-than-35000-swedes-sign-up-to-gambling-self-exclusion-registry/">
                Swedish self-exclusion system
              </a>{" "}
              can be found through their respective platforms. Self-exclusion
              programs, such as GamStop, allow individuals to voluntarily
              restrict their access to online gambling. These programs are
              prevalent in the UK and Sweden. Additionally, applications like{" "}
              <a className="underline" href="https://betblocker.org/">
                BetBlocker
              </a>{" "}
              and{" "}
              <a className="underline" href="https://gamban.com/">
                Gamban
              </a>{" "}
              can be installed on devices to block access to gambling sites.
            </p>
            <h2 className="font-bold text-white my-5">
              <strong>Anonymous Assistance</strong>
            </h2>
            <p>
              For those preferring anonymity, various organizations offer
              support for problem gamblers. These include 24/7 helplines,
              workshops, and therapy sessions.
            </p>
            <h2 className="font-bold text-white my-5">
              <strong>Support Organizations</strong>
            </h2>
            <p>
              There are numerous support groups available globally, for example:{" "}
              <a
                className="underline"
                href="http://www.gamblersanonymous.org/ga/">
                gamblersanonymous.org
              </a>
              ,{" "}
              <a
                className="underline"
                href="https://www.responsiblegambling.org/">
                responsiblegambling.org
              </a>
              , <a href="https://www.gamtalk.org/">gamtalk.org</a>.
            </p>
            <img
              className="my-6"
              src="/images/gambling-help-1.png"
              alt="logo gambling help 1"
            />
            <p>
              On a national level you can find{" "}
              <a className="underline" href="http://ncpgambling.org">
                ncpgambling.org
              </a>
              ,{" "}
              <a
                className="underline"
                href="http://responsiblegambling.vic.gov.au">
                responsiblegambling.vic.gov.au
              </a>
              , <a href="http://gamcare.org.uk">gamcare.org.uk</a>,{" "}
              <a className="underline" href="http://problemgambling.ca">
                problemgambling.ca
              </a>
              ,{" "}
              <a className="underline" href="http://pgf.nzand">
                pgf.nzand
              </a>{" "}
              and many others.
            </p>
            <img
              className="my-6"
              src="/images/gambling-help-2.png"
              alt="logo gambling help 2"
            />
          </div>
        </div>
      </div>

      {/* Slogan section */}
      {(process.env.NEXT_PUBLIC_APP_NAME === "Vybeful" ||
        process.env.NEXT_PUBLIC_APP_NAME === "Festicket") && (
        <div
          className="w-full md:h-800px h-300px"
          style={{
            backgroundImage: "url(/images/slogan_bg.jpg)",
            backgroundSize: "cover",
            backgroundPosition: "center center",
          }}></div>
      )}

      <Footer />
    </div>
  );
};

export default GamblingHelp;
